import React from "react";
import styled from "styled-components";
import { Paragraph16Bold } from "../../../common/Typography/Paragraph16";
import Card from "../../../common/components/Card/Card";
import { Paragraph14Regular } from "../../../common/Typography/Paragraph14";
import { useTranslation } from "react-i18next";
import Carousel from "../../../common/components/Carousel/Carousel";
import { breakpoints } from "./relatedCarouselBreakpoints";
import { CarouselNavigationEnum } from "../../../common/components/Carousel/CarouselNavigration";
import { Truncate } from "../../../common/components/styled";
import Typography from "../../../common/components/Typography/Typography";
import EmptyState from "../../../common/components/EmptyState/EmptyState";
import Button from "../../../common/components/Buttons/Button";
import { routes } from "../../../routes";
import { useRouter } from "next/router";
import SeeAllCard, {
  SeeAllCardLabel,
} from "../../../common/components/Carousel/SeeAllCard";
import { getHeading6BoldTypography } from "../../../common/Typography";
import { IRelatedSectionItem } from "../../../common/commonTypes";
import StaticLink from "../../../common/components/StaticLink/StaticLink";
import { IMAGE_SIZES } from "../../../constants/imageSizes";
import Cover from "../../../common/components/Cover";

const CARD_WIDTH = 190;
const IMAGE_SIZE = IMAGE_SIZES.trackCoverArt.small;

const Root = styled.div`
  margin-bottom: 34px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding-right: 0;
  }
`;

const StyledSeeAllCard = styled(SeeAllCard)`
  width: ${CARD_WIDTH}px;
  padding: 20px;

  ${SeeAllCardLabel} {
    ${getHeading6BoldTypography};
  }
`;

const StyledCard = styled(Card)`
  position: relative;
  width: ${CARD_WIDTH}px;
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 20px;

  div {
    align-self: flex-start;

    white-space: nowrap;
  }
`;

const CardTitle = styled(Paragraph16Bold)``;

const Subtext = styled(Paragraph14Regular)`
  color: ${(props) => props.theme.greyColor};
`;

const ImageWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const CoverMedia = styled(Cover)`
  width: ${IMAGE_SIZE}px;
  height: ${IMAGE_SIZE}px;
  border-radius: ${(props) => props.theme.borderRadius.md};
`;

const EmptyTitle = styled(Typography)`
  margin-bottom: 34px;
`;

interface Props {
  title: string;
  items: IRelatedSectionItem[];
  seeAllUrl: string;
  isLoading: boolean;
  emptyTitle: string;
  emptyDescription: string;
}

const RelatedSection = (props: Props) => {
  const { t } = useTranslation();
  const router = useRouter();

  if (props.items.length === 0) {
    return (
      <Root>
        <EmptyTitle heading weight="500">
          {props.title}
        </EmptyTitle>
        <EmptyState
          titleKey={props.emptyTitle}
          descriptionKey={props.emptyDescription}
          actionButton={
            <Button
              text={t("to-marketplace")}
              onClick={() => {
                router.push(routes.marketplace.root);
              }}
            />
          }
        />
      </Root>
    );
  }

  return (
    <Root>
      <Carousel
        title={props.title}
        items={props.items}
        renderItem={(item) => (
          <StaticLink href={item.redirectUrl}>
            <StyledCard>
              <ImageWrapper>
                <CoverMedia
                  url={item?.image?.url}
                  mime={item?.image?.mime}
                  size={IMAGE_SIZE}
                />
              </ImageWrapper>
              <Truncate>
                <CardTitle>{item.text}</CardTitle>
                <Subtext>{item.subtext}</Subtext>
              </Truncate>
            </StyledCard>
          </StaticLink>
        )}
        keyExtractor={(item) => item.key}
        isLoading={props.isLoading}
        redirectUrl={props.seeAllUrl}
        slidesPerView={1}
        spaceBetween={34}
        breakpoints={breakpoints}
        navigationType={CarouselNavigationEnum.SEE_ALL}
        seeAllComponent={
          <StyledSeeAllCard
            to={props.seeAllUrl}
            label={t("related-section-see-all")}
          />
        }
      />
    </Root>
  );
};

export default RelatedSection;
